import { useDispatch, useSelector } from 'react-redux'
import type { KeyboardEvent } from 'react'
import type { AppDispatch, RootState } from '@/redux'
import { dashboardCreationActions } from '@/redux/slice/dashboardCreationSlice'
import { prefetchAvailableCredentialsForApp } from '@/services/DashboardChatService'
import { flowTransitionThunk } from '@/services/DashboardFlowTransitionService'
import { buildSourcePickTemplates } from '@/services/transitionTemplates'
import { DISABLED_SOURCE_APPS } from './sourceAppCatalog'
import { AppName } from '@/constants/application/application'
import { ChevronRight } from 'lucide-react'

/**
 * SourcePickerBlock — FSM gate: awaiting_source
 *
 * Mockup: designs/ai-dashboard-platform-v3/2026-04-23-03-source-picker-v3.html
 * Brand guide: §12 (Data Source Card), §29 (Disabled App Source Card)
 * TDD: §3d.1.C
 *
 * Change A (hardcoded catalog — SOURCE_APP_CATALOG):
 * - CampMinder: featured full-width, enabled — Recommended badge
 * - SurveyMonkey, CampBrain: disabled 2-col grid (first row)
 * - CampDoc, CampSite:       disabled 2-col grid (second row)
 * - Google Sheets:            disabled full-width (col-span-2, last row)
 *
 * Disabled cards: <div role="button" aria-disabled="true" tabIndex={0}>
 *   opacity-75 cursor-not-allowed bg-[#FAFAFA] per brand guide §29.
 *   NOT <button disabled> — that removes the element from the accessibility tree.
 *   Enter / Space key presses on disabled cards are explicitly swallowed (no FSM action).
 */

// ─── DisabledSourceCard ────────────────────────────────────────────────────────

interface DisabledSourceCardProps {
  name: string
  logo: string | undefined
  /** If true, spans both columns in the 2-col grid (Google Sheets). */
  fullWidth: boolean
}

export const AppLogo = ({ logoURL }: { logoURL: string | undefined }) => {
  if (logoURL) {
    return (
      <div className="flex items-center justify-center rounded-lg border-2 border-border h-10 p-2 bg-foreground">
        <img src={logoURL} className='h-4' />
      </div>
    )
  }
  return null
}

/**
 * Disabled source card — brand guide §29.
 *
 * Rendered as <div role="button" aria-disabled="true" tabIndex={0}> so the
 * card is discoverable by screen readers and reachable via keyboard Tab.
 * Enter / Space are swallowed so keyboard activation has no FSM effect.
 *
 * NOT <button disabled> — that removes the element from the accessibility tree.
 */
function DisabledSourceCard({ name, logo, fullWidth }: DisabledSourceCardProps) {
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
    }
  }

  return (
    <div
      role="button"
      aria-disabled="true"
      tabIndex={0}
      aria-label={`${name} — coming soon`}
      onKeyDown={handleKeyDown}
      className={`flex items-center gap-2.5 px-3 py-[11px] rounded-[10px] border-[1.5px] border-border bg-[#FAFAFA] opacity-75 cursor-not-allowed text-left select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusRing focus-visible:ring-offset-2${fullWidth ? ' col-span-2' : ''}`}
    >
      <div className='opacity-50'>
        <AppLogo logoURL={logo} />
      </div>
      <span className="flex-1 text-sm font-semibold text-muted-foreground">{name}</span>
      <span className="text-[10px] font-semibold px-[7px] py-0.5 rounded-[10px] bg-[#F1F5F9] text-muted-foreground border border-border whitespace-nowrap flex-shrink-0">
        Coming soon
      </span>
    </div>
  )
}

// ─── SourcePickerBlock ─────────────────────────────────────────────────────────

export const SourcePickerBlock = () => {
  const dispatch: AppDispatch = useDispatch()
  const token = useSelector((s: RootState) => s.user.token)
  const { applications } = useSelector((s: RootState) => s.application)
  const dashboardId = useSelector((s: RootState) => s.dashboardCreation.dashboardId)

  // The source-pick gate cannot fire its FSM transition without BOTH a token
  // (auth) and a dashboardId (URL/Redux). The button is rendered disabled
  // until both are present — gating prevents a bootstrap-race where the
  // shell's local hook state un-gates SourcePickerBlock before Redux has
  // propagated dashboardId, leading to an optimistic Redux setFlowState
  // without the corresponding HTTP transition firing (FSM drift). See
  // routing memo `cto1-route-dashboard-creation-fsm-drift.md` (2026-05-28).
  const isReady = Boolean(token && dashboardId)

  const handleSelectCampMinder = () => {
    // Defense-in-depth: if the click somehow reaches the handler before ready
    // (e.g., disabled-button bypass via keyboard or test harness), bail
    // without dispatching ANY Redux update — drift is impossible.
    // React 18 auto-suppresses click on <button disabled>, so this branch is
    // unreachable via the normal DOM path — hence the ignore annotation.
    /* istanbul ignore next */
    if (!isReady) return

    dispatch(dashboardCreationActions.setSourceAppCode('campminder'))
    dispatch(dashboardCreationActions.setFlowState('awaiting_credential'))
    // Deterministic prefetch: load existing credentials for this app so
    // CredentialPickerGateway can route to the existing-picker immediately,
    // without waiting for an AI round-trip (CEO 2026-04-28).
    void dispatch(prefetchAvailableCredentialsForApp(token!, 'campminder'))

    // Slice 1.3: fire flow-transition to record source pick in the chat thread
    // and sync flow_context on the session. flowTransitionThunk reverts the
    // optimistic flowState dispatched above on any HTTP failure — see service
    // thunk file header doc-block (FSM drift fix 2026-05-28).
    const templates = buildSourcePickTemplates('CampMinder')
    const idempotencyKey =
      globalThis.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2)
    void dispatch(
      flowTransitionThunk(
        token!,
        dashboardId!,
        {
          toState: 'awaiting_credential',
          contextDelta: { sourceAppCode: 'campminder' },
          ...templates,
        },
        idempotencyKey
      )
    )
  }

  return (
    <div data-testid="source-picker-block" className="flex flex-col gap-3 w-full">
      <p className="mb-3">Which app are you pulling data from?</p>

      {/* Source cards wrapper */}
      <div
        className="max-w-[580px]"
        role="group"
        aria-label="Select a data source"
      >
        {/* CampMinder: featured full-width enabled button.
            disabled={!isReady} prevents the bootstrap-race where Redux
            dashboardId hasn't propagated yet — see handleSelectCampMinder
            for the rationale. React 18 auto-suppresses onClick on a
            <button disabled> element so the handler also gates with
            `if (!isReady) return` purely as defense-in-depth. */}
        <button
          type="button"
          disabled={!isReady}
          className="flex items-center gap-3.5 w-full text-left px-[15px] py-[13px] rounded-[12px] border-[1.5px] border-border bg-card hover:border-accent hover:bg-accent-background transition-all duration-150 mb-2 focus-visible:ring-2 focus-visible:ring-focusRing focus-visible:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:border-border disabled:hover:bg-card"
          aria-pressed="false"
          aria-label={isReady ? 'CampMinder — select as data source (Recommended)' : 'CampMinder — preparing your dashboard, please wait'}
          onClick={handleSelectCampMinder}
        >
          {/* Logo */}
          <AppLogo logoURL={applications.find(app => app.name === AppName.CAMPMINDER)?.logo} />

          {/* Source info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <span className="text-sm font-semibold text-primary">CampMinder</span>
              <span className="bg-accent-background text-accent text-xs font-semibold px-2 py-0.5 rounded-full border border-[#BFDBFE]">
                Recommended
              </span>
            </div>
            <p className="text-xs text-secondary">
              Enrollment, financials, bunks, staff — all your CampMinder data
            </p>
          </div>

          {/* Arrow */}
          <ChevronRight />
        </button>

        {/* 2-col grid: 5 disabled sources (driven by SOURCE_APP_CATALOG) */}
        <div
          className="grid grid-cols-2 gap-2"
          role="group"
          aria-label="Coming soon — additional data sources"
        >
          {DISABLED_SOURCE_APPS.map(app => {
            const logoURL = applications.find(a => a.name.toLowerCase() === app.code)?.logo
            if (!logoURL) return null
            // `fullWidth` is an optional member of the union — narrow via `in` so the
            // literal-union disabled entries each compile cleanly.
            const isFullWidth = 'fullWidth' in app && app.fullWidth === true
            return (
              <DisabledSourceCard
                key={app.code}
                name={app.name}
                logo={logoURL}
                fullWidth={isFullWidth}
              />
            )
          })}
        </div>
      </div>
    </div>
  )
}
